package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.motion.AnimatedHeight
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FadeInKeyframes
import com.sddamico.components.motion.rememberReducedMotion
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.w3c.dom.HTMLElement
import kotlin.js.json
import com.sddamico.state.LocalAuthActions
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

val SignInStepForwardKeyframes = Keyframes {
    from { Modifier.opacity(0).translateX(12.px) }
    to { Modifier.opacity(1).translateX(0.px) }
}

val SignInStepBackKeyframes = Keyframes {
    from { Modifier.opacity(0).translateX((-12).px) }
    to { Modifier.opacity(1).translateX(0.px) }
}

@InitRoute
fun initSignIn(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Sign In", requiresAuth = false))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun SignInPage() {
    val authState = LocalAuthState.current
    val authActions = LocalAuthActions.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    // Redirect if already logged in
    LaunchedEffect(authState.user) {
        if (!authState.loading && authState.user != null) {
            val returnTo = ctx.route.params["returnTo"] ?: "/clubhouse"
            ctx.router.tryRoutingTo(returnTo)
        }
    }

    var step by remember { mutableStateOf(1) } // 1 = email, 2 = code
    var email by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var failedAttempts by remember { mutableStateOf(0) }
    var inputElement by remember { mutableStateOf<HTMLElement?>(null) }
    val reducedMotion = rememberReducedMotion()

    // A wrong code shakes the field, the same "no" a password box gives on a desktop login.
    LaunchedEffect(failedAttempts) {
        if (failedAttempts == 0 || reducedMotion) return@LaunchedEffect
        inputElement?.asDynamic()?.animate(
            arrayOf(0, -6, 6, -4, 4, -2, 0).map { json("transform" to "translateX(${it}px)") }.toTypedArray(),
            json("duration" to 320, "easing" to Ease.Out.css),
        )
    }

    val submitForm: () -> Unit = {
        if (!loading) {
            scope.launch {
                error = null
                loading = true
                try {
                    if (step == 1) {
                        authActions.sendOtp(email.trim())
                        step = 2
                    } else {
                        authActions.verify(email.trim(), code.trim())
                        val returnTo = ctx.route.params["returnTo"] ?: "/clubhouse"
                        ctx.router.tryRoutingTo(returnTo)
                    }
                } catch (e: ApiException) {
                    error = e.errorMessage
                    failedAttempts++
                } catch (e: Exception) {
                    error = "Something went wrong. Please try again."
                    failedAttempts++
                }
                loading = false
            }
        }
    }

    Box(
        Modifier.fillMaxWidth().padding(top = 4.cssRem),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            Modifier
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(8.px)
                .padding(2.cssRem)
                .width(24.cssRem)
                .maxWidth(100.percent),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            val stepsShown = remember { mutableSetOf<Int>() }
            // The first step arrives with the page's own entrance; only later step changes animate.
            val animateStepEntrance = remember(step) { stepsShown.isNotEmpty().also { stepsShown.add(step) } }
            // The card resizes between the email and code steps instead of snapping.
            AnimatedHeight {
                key(step) {
                    // Going on to the code slides in from the right; going back to the email slides in from the left.
                    val stepKeyframes = when {
                        reducedMotion -> FadeInKeyframes
                        step == 2 -> SignInStepForwardKeyframes
                        else -> SignInStepBackKeyframes
                    }
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .then(
                                if (animateStepEntrance) {
                                    Modifier.animation(stepKeyframes.toAnimation(Durations.ENTER.ms, Ease.Out.fn))
                                } else {
                                    Modifier
                                }
                            ),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Title
                        SpanText(
                            if (step == 1) "Sign In" else "Enter Code",
                            Modifier
                                .fontFamily(*HeadingFontFamily)
                                .fontSize(1.5.cssRem)
                                .fontWeight(FontWeight.Bold)
                                .color(SitePalette.text)
                        )

                        SpanText(
                            if (step == 1) "Enter your email to receive a sign-in code"
                            else "We sent a 6-digit code to $email",
                            Modifier
                                .fontSize(0.8125.cssRem)
                                .color(SitePalette.textSecondary)
                                .margin(top = 0.5.cssRem, bottom = 1.5.cssRem)
                                .textAlign(TextAlign.Center)
                        )

                        if (step == 1) {
                            // Email input
                            Input(InputType.Email) {
                                placeholder("you@example.com")
                                value(email)
                                onInput { email = it.value }
                                onKeyDown { if (it.key == "Enter") submitForm() }
                                ref { element ->
                                    inputElement = element
                                    onDispose { inputElement = null }
                                }
                                style {
                                    property("width", "100%")
                                    property("padding", "0.75rem")
                                    property("background-color", "#0F1216")
                                    property("border", "1px solid #2A3038")
                                    property("border-radius", "8px")
                                    property("color", "#E8E6E1")
                                    property("font-size", "0.9375rem")
                                    property("outline", "none")
                                }
                            }
                        } else {
                            // Code input
                            Input(InputType.Text) {
                                placeholder("000000")
                                value(code)
                                onInput {
                                    val filtered = it.value.filter { c -> c.isDigit() }.take(6)
                                    code = filtered
                                    if (filtered.length == 6) submitForm()
                                }
                                onKeyDown { if (it.key == "Enter") submitForm() }
                                ref { element ->
                                    inputElement = element
                                    onDispose { inputElement = null }
                                }
                                attr("maxlength", "6")
                                attr("autocomplete", "one-time-code")
                                style {
                                    property("width", "100%")
                                    property("padding", "0.75rem")
                                    property("background-color", "#0F1216")
                                    property("border", "1px solid #2A3038")
                                    property("border-radius", "8px")
                                    property("color", "#E8E6E1")
                                    property("font-size", "1.5rem")
                                    property("text-align", "center")
                                    property("letter-spacing", "0.5rem")
                                    property("outline", "none")
                                    property("font-variant-numeric", "tabular-nums")
                                }
                            }
                        }

                        // Error message
                        error?.let {
                            SpanText(
                                it,
                                Modifier
                                    .color(SitePalette.error)
                                    .fontSize(0.8125.cssRem)
                                    .margin(top = 0.75.cssRem)
                            )
                        }

                        // Submit button
                        Button(
                            attrs = Modifier
                                .fillMaxWidth()
                                .margin(top = 1.cssRem)
                                .backgroundColor(SitePalette.primary)
                                .color(Colors.White)
                                .borderRadius(8.px)
                                .padding(topBottom = 0.75.cssRem)
                                .fontSize(0.9375.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .cursor(if (loading) Cursor.Default else Cursor.Pointer)
                                .border(0.px)
                                .opacity(if (loading) 0.6 else 1.0)
                                .toAttrs {
                                    if (loading) disabled()
                                    onClick { submitForm() }
                                }
                        ) {
                            Text(
                                when {
                                    loading && step == 1 -> "Sending..."
                                    loading && step == 2 -> "Verifying..."
                                    step == 1 -> "Send Code"
                                    else -> "Verify Code"
                                }
                            )
                        }

                        // Secondary actions
                        if (step == 2) {
                            Div(
                                Modifier
                                    .margin(top = 1.cssRem)
                                    .display(DisplayStyle.Flex)
                                    .gap(1.cssRem)
                                    .toAttrs()
                            ) {
                                Span(
                                    Modifier
                                        .color(SitePalette.accent)
                                        .fontSize(0.8125.cssRem)
                                        .cursor(Cursor.Pointer)
                                        .onClick {
                                            step = 1
                                            code = ""
                                            error = null
                                        }
                                        .toAttrs()
                                ) {
                                    Text("Use a different email")
                                }

                                Span(
                                    Modifier
                                        .color(SitePalette.textSecondary)
                                        .fontSize(0.8125.cssRem)
                                        .cursor(Cursor.Pointer)
                                        .onClick {
                                            scope.launch {
                                                error = null
                                                loading = true
                                                try {
                                                    authActions.sendOtp(email.trim())
                                                    error = null
                                                } catch (e: ApiException) {
                                                    error = e.errorMessage
                                                }
                                                loading = false
                                            }
                                        }
                                        .toAttrs()
                                ) {
                                    Text("Resend code")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
